import { Controller } from "@hotwired/stimulus"

// Cache so that we reuse options when the same template is picked again
const classroomSessionReassignCache = window.classroomSessionReassignCache || (window.classroomSessionReassignCache = new Map());

export default class extends Controller {
  static targets = [
    "templateSelect", 
    "templateSelectContainer", 
    "templateDisplay", 
    "templateDisplayContainer", 
    "sessionSelect"
  ]

  static values = {
    sessionId: Number,
    attendanceId: Number,
    attendanceType: String,
    initialTemplateId: String,
    preloadedOptions: Array,
    preloadedOldOptions: Array,
    moveSessionId: String
  }

  connect() {
    this.loadAvailableSessions();
  }

  showTemplateSelect(event) {
    event.preventDefault();
    if (this.hasTemplateDisplayContainerTarget) {
      this.templateDisplayContainerTarget.classList.add("u-hidden");
    }
    if (this.hasTemplateSelectContainerTarget) {
      this.templateSelectContainerTarget.classList.remove("u-hidden");
    }
    if (this.hasTemplateSelectTarget) {
      this.templateSelectTarget.focus();
    }
  }

  templateChanged() {
    const selectedOption = this.templateSelectTarget.selectedOptions[0];
    this.selectedTemplateId = this.templateSelectTarget.value;
    this.selectedTemplateLabel = selectedOption ? selectedOption.textContent : "";

    if (this.hasTemplateDisplayTarget) {
      this.templateDisplayTarget.textContent = this.selectedTemplateLabel;
    }
    if (this.hasTemplateSelectContainerTarget) {
      this.templateSelectContainerTarget.classList.add("u-hidden");
    }
    if (this.hasTemplateDisplayContainerTarget) {
      this.templateDisplayContainerTarget.classList.remove("u-hidden");
    }

    this.loadAvailableSessions({ keepInitialSession: false, autoSelectFirstSession: true });
  }

  loadAvailableSessions({ keepInitialSession = true, autoSelectFirstSession = false } = {}) {
    if (!this.hasTemplateSelectTarget || !this.hasSessionSelectTarget) {
      return;
    }

    const templateId = this.templateSelectTarget.value;
    const selectedOption = this.templateSelectTarget.selectedOptions[0];
    const templateLabel = selectedOption ? selectedOption.textContent : "";
    this.selectedTemplateId = templateId;
    this.selectedTemplateLabel = templateLabel;
    this.resetSessionOptions();

    if (!templateId) {
      return;
    }

    // Pupils with attendance type of both shouldn't be allowed
    // Just return no available sessions for these freaks of nature for now
    if (this.attendanceTypeValue === "both") {
      this.applyOptions({
        available_sessions: [],
        previous_sessions: []
      }, { templateId, templateLabel, keepInitialSession, autoSelectFirstSession });
      return;
    }

    // On initial load we can preload the reassign dropdown options 
    // We always know the selected template and each attendee's attendance type
    if ((this.preloadedOptionsValue.length > 0 || this.preloadedOldOptionsValue.length > 0) && templateId === this.initialTemplateIdValue) {
      this.applyOptions({
        available_sessions: this.preloadedOptionsValue || [],
        previous_sessions: this.preloadedOldOptionsValue || []
      }, { templateId, templateLabel, keepInitialSession, autoSelectFirstSession });
      return;
    }

    const cacheKey = `${this.sessionIdValue}|${this.attendanceTypeValue}|${templateId}`;
    if (classroomSessionReassignCache.has(cacheKey)) {
      this.applyOptions(classroomSessionReassignCache.get(cacheKey), { templateId, templateLabel, keepInitialSession, autoSelectFirstSession });
      return
    }

    this.sessionSelectTarget.disabled = true;

    fetch(`/schools/classroom/sessions/attendance/${this.attendanceIdValue}/available-reassign-sessions?template_id=${templateId}&attendance_type=${this.attendanceTypeValue}`, {
      headers: { "Accept": "application/json" }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load reassign options");
        }
        return response.json();
      })
      .then((data) => {
        classroomSessionReassignCache.set(cacheKey, data);
        this.applyOptions(data, { templateId, templateLabel, keepInitialSession, autoSelectFirstSession });
      })
      .catch(() => {
        this.showNoSessions("Unable to load sessions");
        this.dispatchSessionChange();
        this.deferTemplateDisplayUpdate(templateLabel);
      })
      .finally(() => {
        this.sessionSelectTarget.disabled = false;
      })
  }

  resetSessionOptions() {
    this.sessionSelectTarget.innerHTML = "";

    const prompt = document.createElement("option");
    prompt.value = "";
    prompt.textContent = "Select a new session";
    this.sessionSelectTarget.appendChild(prompt);
  }

  applyOptions(data, { templateId, templateLabel, keepInitialSession, autoSelectFirstSession }) {
    if (templateId !== this.selectedTemplateId) {
      return;
    }

    this.resetSessionOptions();
    this.updateTemplateDisplay(templateLabel);

    const availableSessions = data.available_sessions || [];
    const previousSessions = data.previous_sessions || [];
    const allSessions = availableSessions.concat(previousSessions);

    if (availableSessions.length === 0 && previousSessions.length === 0) {
      this.showNoSessions("No make-up sessions available");
      this.dispatchSessionChange();
      this.deferTemplateDisplayUpdate(templateLabel);
      return;
    }

    if (availableSessions.length > 0) {
      this.appendGroup("Available sessions", availableSessions);
    }

    if (previousSessions.length > 0) {
      this.appendGroup("Previously available sessions", previousSessions);
    }

    if (keepInitialSession && this.moveSessionIdValue && this.sessionOptionExists(this.moveSessionIdValue)) {
      this.sessionSelectTarget.value = this.moveSessionIdValue;
    } else if (autoSelectFirstSession && allSessions.length > 0) {
      this.sessionSelectTarget.value = allSessions[0].id;
    }

    this.dispatchSessionChange();
    this.deferTemplateDisplayUpdate(templateLabel);
  }

  appendGroup(label, sessions) {
    const group = document.createElement("optgroup");
    group.label = label;

    sessions.forEach((session) => {
      const option = document.createElement("option");
      option.value = session.id;
      option.textContent = session.label;
      if (session.in_person !== undefined) option.dataset.inPerson = session.in_person;
      if (session.remote !== undefined) option.dataset.remote = session.remote;
      if (session.duration_minutes !== undefined) option.dataset.durationMinutes = session.duration_minutes;
      group.appendChild(option);
    })

    this.sessionSelectTarget.appendChild(group);
  }

  showNoSessions(message) {
    const option = document.createElement("option");
    option.value = "";
    option.textContent = message;
    option.disabled = true;
    this.sessionSelectTarget.appendChild(option);
  }

  updateTemplateDisplay(label) {
    if (this.hasTemplateDisplayTarget && label) {
      this.templateDisplayTarget.textContent = label;
    }
  }

  deferTemplateDisplayUpdate(label) {
    window.setTimeout(() => this.updateTemplateDisplay(label), 0);
  }

  sessionOptionExists(value) {
    return Array.from(this.sessionSelectTarget.options).some((option) => option.value === value.toString());
  }

  dispatchSessionChange() {
    this.sessionSelectTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }
}
